'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect } from 'react';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import { setListsAndListItems } from '../../slices/lists';
import type ListItem from '../../types/ListItem';
import { useAppDispatch } from '../../utils/redux';
import getListsAndListItems from '../../utils/requests';

const SignUpModal = dynamic(
  () => {
    if (typeof window !== 'undefined' && 'Cypress' in window) {
      console.log(`[ListSignUpModal] SignUpModal import start at ${Math.round(performance.now())}ms`, { readyState: document.readyState });
    }
    return import('@alltrails/user-auth/components/SignUpModal').then(mod => {
      if (typeof window !== 'undefined' && 'Cypress' in window) {
        console.log(`[ListSignUpModal] SignUpModal import resolved at ${Math.round(performance.now())}ms`, { readyState: document.readyState });
      }
      return mod;
    });
  },
  {
    ssr: false,
    loading: () => {
      if (typeof window !== 'undefined' && 'Cypress' in window) {
        console.log(`[ListSignUpModal] SignUpModal loading at ${Math.round(performance.now())}ms`, { readyState: document.readyState });
      }
      return null;
    }
  }
);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  item?: { type: ListItem['type']; id: number };
  onSuccess?: () => void;
  preventAutoInitialize?: boolean;
};

/**
 * The sign up modal a logged out user gets when they try to save something to a list.
 *
 * It owns every part of that flow that is specific to saving — the Favorite trigger, the deep link
 * back to the item, and loading the new user's lists — so a save button can render it directly
 * rather than being handed modal markup by `useReduxListItems`.
 */
export default function ListSignUpModal({ isOpen, onClose, item, onSuccess, preventAutoInitialize }: Props) {
  const dispatch = useAppDispatch();

  const handleSuccess = useCallback(async () => {
    if (!preventAutoInitialize) {
      dispatch(setListsAndListItems(await getListsAndListItems()));
    }
    onSuccess?.();
  }, [dispatch, onSuccess, preventAutoInitialize]);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Cypress' in window) {
      console.log(`[ListSignUpModal] isOpen at ${Math.round(performance.now())}ms`, {
        isOpen,
        itemId: item?.id,
        itemType: item?.type,
        readyState: document.readyState
      });
    }
  }, [isOpen, item?.id, item?.type]);

  if (!isOpen) {
    return null;
  }

  const deeplinkData: DeeplinkData | undefined = item?.type === 'trail' ? { pageId: item.id, pageType: 'trail' } : undefined;

  return (
    <SignUpModal
      autoClose
      closeModal={onClose}
      deeplinkData={deeplinkData}
      isOpen
      onSuccess={handleSuccess}
      trigger="Favorite"
      upsellTrigger="Favorite"
    />
  );
}
